import { yupResolver } from '@hookform/resolvers/yup';
import { Close, Delete, Videocam } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { hireflixCta, hireflixCtaHover } from '@styles/colors';
import { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, Resolver } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { useUpdateHireflixVideoInterview } from '../hooks/useUpdateHireflixVideoInterview';

type Question = {
  id?: number | string;
  title: string;
  description: string;
};

type FormValues = {
  questions: Question[];
};

const schema = yup.object().shape({
  questions: yup
    .array()
    .of(
      yup.object().shape({
        title: yup.string().required('Question is required'),
        description: yup.string().required('Answer is required'),
      }),
    )
    .min(1, 'At least one question is required'),
});

const HireflixUpdateVideoInterviewModal = ({
  open,
  onClose,
  job,
  onEditQuestion,
  templateId,
}: any) => {
  const [errorText, setErrorText] = useState<string>('');

  const { updateHireflixVideoInterview, isUpdatingVideoInterviewLoading } =
    useUpdateHireflixVideoInterview({
      setErrorText,
    });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema) as Resolver<FormValues>,
    defaultValues: { questions: [{ title: '', description: '' }] },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'questions',
  });

  useEffect(() => {
    const initial = (job?.questions || []).map((q: any) => ({
      title: q.title || '',
      description: q.description || '',
    }));

    if (initial.length > 0) {
      replace(initial);
      reset({ questions: initial });
    } else {
      replace([{ title: '', description: '' }]);
      reset({ questions: [{ title: '', description: '' }] });
    }
  }, [job, replace, reset]);

  const onSave = (values: FormValues) => {
    const payload = {
      template_id: templateId,
      job_id: job?.id,
      questions: values.questions,
    };

    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data && data?.status === 200) {
          toast.success(
            data.message || 'HireFlix Interview Updated successfully!',
          );
          setErrorText('');
          if (onEditQuestion) onEditQuestion(values.questions);
          onClose();
        } else {
          setErrorText(data?.message || 'Unable to update position');
        }
      },
    };

    updateHireflixVideoInterview({ ...payload }, options);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={onClose}
        >
          <Close />
        </IconButton>
        <br />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '64px',
            height: '64px',
            margin: '0 auto',
            borderRadius: '100%',
            backgroundColor: 'rgb(243, 232, 255)',
            mb: 1,
          }}
        >
          <Videocam sx={{ fontSize: 40, color: hireflixCta }} />
        </Box>
        <Typography variant="h3" fontWeight="600" sx={{ p: 3 }}>
          Edit Interview Questions
          <Typography mt={1}>({job?.s_title})</Typography>
        </Typography>
      </DialogTitle>

      <DialogContent>
        {errorText && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorText}
          </Alert>
        )}
        {job?.invite_count >= 1 && (
          <Alert
            severity="warning"
            sx={{
              mb: 3,
              backgroundColor: 'rgb(255, 244, 229)',
              color: 'rgb(102, 60, 0)',
              '& .MuiAlert-icon': { color: 'rgb(237, 108, 2)' },
            }}
          >
            You are editing a position that already has 1 candidates invited.{' '}
            <b>Changes will only apply to new candidates invites.</b>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSave)}>
          <Stack spacing={3} py={2}>
            <Stack spacing={2}>
              {fields.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    p: 3,
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      backgroundColor: '#f5f7fa',
                      borderRadius: 6,
                      p: 1.2,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      color="text.primary"
                    >
                      Question #{index + 1}
                    </Typography>

                    {fields.length > 1 && (
                      <Tooltip title="Delete Question" placement="top" arrow>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => remove(index)}
                        >
                          <Delete sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Stack spacing={2}>
                    <Controller
                      name={`questions.${index}.title` as const}
                      control={control}
                      render={({ field: qField }) => (
                        <TextField
                          {...qField}
                          label="Question Title"
                          fullWidth
                          size="small"
                          error={!!errors.questions?.[index]?.title}
                          helperText={errors.questions?.[index]?.title?.message}
                        />
                      )}
                    />

                    <Controller
                      name={`questions.${index}.description` as const}
                      control={control}
                      render={({ field: qField }) => (
                        <TextField
                          {...qField}
                          label="Full Question"
                          fullWidth
                          size="small"
                          multiline
                          rows={3}
                          error={!!errors.questions?.[index]?.description}
                          helperText={
                            errors.questions?.[index]?.description?.message
                          }
                        />
                      )}
                    />
                  </Stack>
                </Box>
              ))}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWarp: 'wrap',
                  pt: 2,
                  pb: 8,
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: '14px',
                    px: 2,
                    textTransform: 'none',
                    borderColor: hireflixCta,
                    color: hireflixCta,
                    '&:hover': {
                      backgroundColor: 'rgb(243, 232, 255)',
                      borderColor: hireflixCta,
                    },
                  }}
                  disabled={
                    isUpdatingVideoInterviewLoading || fields.length >= 5
                  }
                  onClick={() => append({ title: '', description: '' })}
                >
                  + Add Question
                </Button>

                {fields.length >= 5 && (
                  <Typography color="text.secondary" variant="body2">
                    Video interviews allow a maximum of 5 questions
                  </Typography>
                )}
              </Box>
            </Stack>

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="info"
                onClick={onClose}
                disabled={isUpdatingVideoInterviewLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isUpdatingVideoInterviewLoading}
                startIcon={
                  isUpdatingVideoInterviewLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                sx={{
                  backgroundColor: hireflixCta,
                  '&:hover': {
                    backgroundColor: hireflixCtaHover,
                  },
                }}
              >
                Save Changes
              </Button>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default HireflixUpdateVideoInterviewModal;
